import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import "./orderCustomerDisplay.css";

/** Visible short form length before ellipsis (full string shown on hover/tap). */
const SHORT_ADDRESS_MAX_CHARS = 18;

export function toShortAddress(address, maxChars = SHORT_ADDRESS_MAX_CHARS) {
  const full = String(address || "").trim();
  if (!full) return "";
  if (full.length <= maxChars) return full;
  return `${full.slice(0, maxChars).trimEnd()}…`;
}

function isFinePointerEvent(e) {
  // Only treat real mouse hover as hover; ignore touch/pen synthetic mouse events.
  if (e && e.pointerType) return e.pointerType === "mouse";
  if (typeof window === "undefined" || !window.matchMedia) return true;
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

/**
 * Short address by default; full address on hover (desktop) or tap (mobile/tablet).
 * Popover is portaled so table/drawer overflow does not clip it.
 */
export default function AddressReveal({
  address,
  className = "",
  showPin = true,
  naLabel,
  maxChars = SHORT_ADDRESS_MAX_CHARS,
}) {
  const { t, i18n } = useTranslation("translation");
  const na = naLabel ?? t("N/A", "N/A");
  const isRtl = i18n.dir() === "rtl";
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    maxWidth: 280,
    anchorBottom: 0,
    anchorTop: 0,
  });
  const wrapRef = useRef(null);
  const popoverRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const ignoreClickRef = useRef(false);

  const hasAddress = Boolean(address && String(address).trim());
  const full = hasAddress ? String(address).trim() : "";
  const short = hasAddress ? toShortAddress(full, maxChars) : "";

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const updatePosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 6;
    const maxWidth = Math.min(320, window.innerWidth - 16);
    let left = isRtl ? rect.right - maxWidth : rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - maxWidth - 8));
    const top = rect.bottom + gap;
    setCoords({
      top,
      left,
      maxWidth,
      anchorBottom: rect.bottom,
      anchorTop: rect.top,
    });
  }, [isRtl]);

  const openPopover = useCallback(() => {
    clearHoverTimer();
    updatePosition();
    setOpen(true);
  }, [clearHoverTimer, updatePosition]);

  const closePopover = useCallback(() => {
    clearHoverTimer();
    setOpen(false);
  }, [clearHoverTimer]);

  const scheduleClose = useCallback(() => {
    clearHoverTimer();
    hoverTimerRef.current = setTimeout(() => setOpen(false), 160);
  }, [clearHoverTimer]);

  useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  useLayoutEffect(() => {
    if (!open || !popoverRef.current) return;
    const pop = popoverRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - coords.anchorBottom;
    if (spaceBelow < pop.height + 12 && coords.anchorTop > pop.height + 12) {
      setCoords((c) => ({
        ...c,
        top: c.anchorTop - pop.height - 6,
      }));
    }
  }, [open, coords.anchorBottom, coords.anchorTop, full]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocPointer = (e) => {
      const inTrigger = wrapRef.current?.contains(e.target);
      const inPopover = popoverRef.current?.contains(e.target);
      if (!inTrigger && !inPopover) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onReposition = () => updatePosition();
    document.addEventListener("pointerdown", onDocPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      document.removeEventListener("pointerdown", onDocPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
  }, [open, updatePosition]);

  const stopRowNav = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const onTapToggle = useCallback(
    (e) => {
      if (!hasAddress) return;
      e.preventDefault();
      e.stopPropagation();
      // After a mouse-hover open, the following click should not immediately close.
      if (ignoreClickRef.current) {
        ignoreClickRef.current = false;
        return;
      }
      if (open) closePopover();
      else openPopover();
    },
    [hasAddress, open, openPopover, closePopover]
  );

  if (!hasAddress) {
    return (
      <span
        className={`oh-address-reveal oh-address-reveal--na ${className}`.trim()}
        onClick={stopRowNav}
        onPointerDown={stopRowNav}
      >
        {showPin ? (
          <MapPin
            size={14}
            strokeWidth={1.75}
            className="oh-address-reveal__pin"
            aria-hidden
          />
        ) : null}
        <span className="oh-address-reveal__text">{na}</span>
      </span>
    );
  }

  const popover =
    open && typeof document !== "undefined"
      ? createPortal(
          <span
            ref={popoverRef}
            className="oh-address-reveal__popover oh-address-reveal__popover--portal"
            role="tooltip"
            style={{
              top: coords.top,
              left: coords.left,
              maxWidth: coords.maxWidth,
            }}
            onMouseEnter={() => {
              clearHoverTimer();
              setOpen(true);
            }}
            onMouseLeave={scheduleClose}
            onPointerDown={stopRowNav}
          >
            {full}
          </span>,
          document.body
        )
      : null;

  return (
    <>
      <span
        ref={wrapRef}
        className={`oh-address-reveal oh-address-reveal--interactive ${className}`.trim()}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={full}
        onPointerDown={stopRowNav}
        onClick={onTapToggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onTapToggle(e);
        }}
        onPointerEnter={(e) => {
          if (!isFinePointerEvent(e)) return;
          ignoreClickRef.current = true;
          openPopover();
        }}
        onPointerLeave={(e) => {
          if (!isFinePointerEvent(e)) return;
          ignoreClickRef.current = false;
          scheduleClose();
        }}
      >
        {showPin ? (
          <MapPin
            size={14}
            strokeWidth={1.75}
            className="oh-address-reveal__pin"
            aria-hidden
          />
        ) : null}
        <span className="oh-address-reveal__text oh-address-reveal__text--short">
          {short}
        </span>
      </span>
      {popover}
    </>
  );
}
